package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LoadErrorState
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.di.loadGuarded
import br.com.vvrefeicoes.portal.core.util.DateTimeProvider
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.CustomerSatisfaction
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.repository.CustomerSatisfactionRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

class CustomerSatisfactionController(
    private val repository: CustomerSatisfactionRepository,
    private val scope: CoroutineScope,
    private val dateTimeProvider: DateTimeProvider = SystemDateTimeProvider(),
    private val loadError: LoadErrorState = LoadErrorState(),
) {
    var loading by mutableStateOf(true)
        private set

    var records by mutableStateOf<List<CustomerSatisfaction>>(emptyList())
        private set

    var period by mutableStateOf(dateTimeProvider.currentPeriod())
        private set

    private var started = false
    private var unitName: String? = null

    fun init() {
        if (started) return
        started = true
        scope.launch {
            loadError.loadGuarded { load() }
            loading = false
        }
    }

    fun applyUnit(
        name: String,
        authorizedNames: Set<String>,
    ) {
        val resolved = name.takeIf { it.isNotBlank() && it in authorizedNames }.orEmpty()
        if (unitName == resolved) return
        unitName = resolved
        if (started) scope.launch { loadError.loadGuarded { load() } }
    }

    fun applyPeriod(month: MonthPeriod) {
        if (period == month) return
        period = month
        if (started) scope.launch { loadError.loadGuarded { load() } }
    }

    private suspend fun load() {
        val selectedUnit = unitName
        records =
            if (selectedUnit.isNullOrBlank()) {
                emptyList()
            } else {
                repository.load(period, selectedUnit)
            }
    }
}

@Composable
fun rememberCustomerSatisfactionController(): CustomerSatisfactionController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember {
        CustomerSatisfactionController(
            repository = appGraph.customerSatisfactionRepository,
            scope = scope,
            dateTimeProvider = appGraph.dateTimeProvider,
            loadError = appGraph.loadError,
        )
    }
}
